import { motion } from 'framer-motion';
import { useState, useMemo } from 'react';
import { getPageStyles, MOTION } from '../helpers/pageTheme';
import { MdLock } from 'react-icons/md';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
    FaEye,
    FaEyeSlash,
    FaLock,
    FaShieldAlt,
    FaCheck,
    FaExclamationTriangle
} from 'react-icons/fa';
import 'bootstrap/dist/css/bootstrap.min.css';
import axios from 'axios';
import { BASE_API_URL } from '../helpers/Config';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

const ChangePassword = ({ theme = 'dark' }) => {
    const [showPasswords, setShowPasswords] = useState({
        current: false,
        new: false,
        confirm: false
    });
    const [passwordStrength, setPasswordStrength] = useState({
        score: 0,
        feedback: []
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    const navigate = useNavigate();

    // derive dark mode from prop
    const isDarkMode = theme === 'dark';
    const styles = useMemo(() => getPageStyles(isDarkMode), [isDarkMode]);

    // Validation Schema
    const validationSchema = Yup.object({
        currentPassword: Yup.string()
            .required('Current password is required')
            .min(6, 'Password must be at least 6 characters'),
        newPassword: Yup.string()
            .required('New password is required')
            .min(8, 'Password must be at least 8 characters')
            .matches(/[a-z]/, 'Must contain at least one lowercase letter')
            .matches(/[A-Z]/, 'Must contain at least one uppercase letter')
            .matches(/[0-9]/, 'Must contain at least one number')
            .matches(/[!@#$%^&*]/, 'Must contain at least one special character (!@#$%^&*)')
            .notOneOf(
                [Yup.ref('currentPassword')],
                'New password must be different from current password'
            ),
        confirmPassword: Yup.string()
            .required('Please confirm your password')
            .oneOf([Yup.ref('newPassword')], 'Passwords must match'),
        logoutFromSession: Yup.boolean().oneOf(
            [true],
            'You must confirm to logout from this session'
        )
    });

    // Formik setup
    const formik = useFormik({
        initialValues: {
            currentPassword: '',
            newPassword: '',
            confirmPassword: '',
            logoutFromSession: false
        },
        validationSchema,
        onSubmit: async (values) => {
            setIsSubmitting(true);

            try {
                const response = await axios.post(`${BASE_API_URL}/account/change-password`, {
                    token: localStorage.getItem('a5srf4wer7w1cd'),
                    old: values.currentPassword,
                    new: values.newPassword,
                    confirm: values.confirmPassword
                });
                const data = response?.data;
                console.log(data);

                if (data?.error) {
                    if (data?.error === 'session expired') {
                        localStorage.clear('a5srf4wer7w1cd');
                        navigate('/login');
                    } else {
                        toast.error(data?.error);
                    }
                }
                if (data?.msg) {
                    toast.success(data?.msg);
                    localStorage.clear('a5srf4wer7w1cd');
                    navigate('/');
                }
            } catch (error) {
                console.log(error);
            } finally {
                setIsSubmitting(false);
                formik.resetForm();
                setPasswordStrength({ score: 0, feedback: [] });
            }
        }
    });

    // Check password strength for real-time feedback
    const checkPasswordStrength = (password) => {
        const feedback = [];
        let score = 0;

        if (password.length >= 8) {
            score += 1;
        } else {
            feedback.push('At least 8 characters');
        }

        if (/[A-Z]/.test(password)) {
            score += 1;
        } else {
            feedback.push('One uppercase letter');
        }

        if (/[a-z]/.test(password)) {
            score += 1;
        } else {
            feedback.push('One lowercase letter');
        }

        if (/[0-9]/.test(password)) {
            score += 1;
        } else {
            feedback.push('One number');
        }

        if (/[!@#$%^&*]/.test(password)) {
            score += 1;
        } else {
            feedback.push('One special character');
        }

        setPasswordStrength({ score, feedback });
    };

    // Handle password change with strength check
    const handlePasswordChange = (e) => {
        formik.handleChange(e);
        if (e.target.name === 'newPassword') {
            checkPasswordStrength(e.target.value);
        }
    };

    const togglePasswordVisibility = (field) => {
        setShowPasswords((prev) => ({
            ...prev,
            [field]: !prev[field]
        }));
    };

    const getStrengthColor = () => {
        if (passwordStrength.score <= 2) return 'danger';
        if (passwordStrength.score <= 3) return 'warning';
        return 'success';
    };

    const getStrengthText = () => {
        if (passwordStrength.score <= 2) return 'Weak';
        if (passwordStrength.score <= 3) return 'Medium';
        return 'Strong';
    };

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                duration: 0.6,
                staggerChildren: 0.1
            }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 15 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.4,
                ease: 'easeOut'
            }
        }
    };

    const isFormValid = () => {
        return formik.isValid && formik.dirty && passwordStrength.score >= 3;
    };

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4 }}
            className="min-vh-100 d-flex align-items-center justify-content-center p-3"
            style={styles.page}
        >
            <div className="w-100" style={{ maxWidth: '420px' }}>
                <motion.div variants={MOTION.container} initial="hidden" animate="visible" className="d-flex flex-column gap-3">
                    <motion.div variants={MOTION.item} style={styles.hero()} className="text-center">
                        <MdLock size={32} className="mb-2" />
                        <h2 className="h5 fw-bold mb-1 text-white">Change Password</h2>
                        <p className="mb-0 small opacity-75">Keep your account secure</p>
                    </motion.div>
                    <motion.div variants={MOTION.item} style={styles.card} className="p-4">
                        <motion.form
                            variants={containerVariants}
                            onSubmit={formik.handleSubmit}
                            noValidate
                        >
                            {/* Current Password */}
                            <motion.div variants={itemVariants} className="mb-3">
                                <label
                                    htmlFor="currentPassword"
                                    className="form-label fw-semibold small"
                                    style={{ color: isDarkMode ? '#e5e7eb' : '#111827' }}
                                >
                                    Current Password
                                </label>
                                <div className="input-group input-group-sm">
                                    <input
                                        id="currentPassword"
                                        name="currentPassword"
                                        type={showPasswords.current ? 'text' : 'password'}
                                        value={formik.values.currentPassword}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        className={`form-control form-control-sm ${formik.touched.currentPassword &&
                                            formik.errors.currentPassword
                                            ? 'is-invalid'
                                            : ''
                                            } ${formik.touched.currentPassword &&
                                                !formik.errors.currentPassword
                                                ? 'is-valid'
                                                : ''
                                            }`}
                                        placeholder="Enter current password"
                                        style={{
                                            fontSize: '0.875rem',
                                            background: isDarkMode ? '#020617' : '#ffffff',
                                            color: isDarkMode ? '#e5e7eb' : '#111827',
                                            borderColor: isDarkMode ? '#334155' : undefined
                                        }}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => togglePasswordVisibility('current')}
                                        className="btn btn-outline-secondary border"
                                        style={{
                                            fontSize: '0.75rem',
                                            background: isDarkMode ? '#020617' : '#ffffff',
                                            color: isDarkMode ? '#e5e7eb' : '#6c757d',
                                            borderColor: isDarkMode ? '#4b5563' : undefined
                                        }}
                                    >
                                        {showPasswords.current ? (
                                            <FaEyeSlash size={14} />
                                        ) : (
                                            <FaEye size={14} />
                                        )}
                                    </button>
                                </div>
                                {formik.touched.currentPassword &&
                                    formik.errors.currentPassword && (
                                        <motion.div
                                            initial={{ opacity: 0, height: 0 }}
                                            animate={{ opacity: 1, height: 'auto' }}
                                            className="text-danger small mt-1 d-flex align-items-center"
                                        >
                                            <FaExclamationTriangle className="me-1" size={10} />
                                            {formik.errors.currentPassword}
                                        </motion.div>
                                    )}
                            </motion.div>

                            {/* New Password */}
                            <motion.div variants={itemVariants} className="mb-3">
                                <label
                                    htmlFor="newPassword"
                                    className="form-label fw-semibold small"
                                    style={{ color: isDarkMode ? '#e5e7eb' : '#111827' }}
                                >
                                    New Password
                                </label>
                                <div className="input-group input-group-sm">
                                    <input
                                        id="newPassword"
                                        name="newPassword"
                                        type={showPasswords.new ? 'text' : 'password'}
                                        value={formik.values.newPassword}
                                        onChange={handlePasswordChange}
                                        onBlur={formik.handleBlur}
                                        className={`form-control form-control-sm ${formik.touched.newPassword &&
                                            formik.errors.newPassword
                                            ? 'is-invalid'
                                            : ''
                                            } ${formik.touched.newPassword &&
                                                !formik.errors.newPassword
                                                ? 'is-valid'
                                                : ''
                                            }`}
                                        placeholder="Enter new password"
                                        style={{
                                            fontSize: '0.875rem',
                                            background: isDarkMode ? '#020617' : '#ffffff',
                                            color: isDarkMode ? '#e5e7eb' : '#111827',
                                            borderColor: isDarkMode ? '#334155' : undefined
                                        }}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => togglePasswordVisibility('new')}
                                        className="btn btn-outline-secondary border"
                                        style={{
                                            fontSize: '0.75rem',
                                            background: isDarkMode ? '#020617' : '#ffffff',
                                            color: isDarkMode ? '#e5e7eb' : '#6c757d',
                                            borderColor: isDarkMode ? '#4b5563' : undefined
                                        }}
                                    >
                                        {showPasswords.new ? (
                                            <FaEyeSlash size={14} />
                                        ) : (
                                            <FaEye size={14} />
                                        )}
                                    </button>
                                </div>

                                {/* Password Strength Meter */}
                                {formik.values.newPassword && (
                                    <motion.div
                                        initial={{ opacity: 0, height: 0 }}
                                        animate={{ opacity: 1, height: 'auto' }}
                                        className="mt-2"
                                    >
                                        <div className="d-flex align-items-center justify-content-between mb-1">
                                            <small
                                                style={{
                                                    fontSize: '0.75rem',
                                                    color: isDarkMode ? '#9ca3af' : '#6c757d'
                                                }}
                                            >
                                                Password Strength:
                                            </small>
                                            <small
                                                className={`fw-semibold text-${getStrengthColor()}`}
                                                style={{ fontSize: '0.75rem' }}
                                            >
                                                {getStrengthText()}
                                            </small>
                                        </div>
                                        <div className="progress" style={{ height: '4px' }}>
                                            <div
                                                className={`progress-bar bg-${getStrengthColor()}`}
                                                style={{
                                                    width: `${(passwordStrength.score / 5) * 100
                                                        }%`
                                                }}
                                            />
                                        </div>
                                        {formik.touched.newPassword &&
                                            formik.errors.newPassword && (
                                                <motion.div
                                                    initial={{ opacity: 0 }}
                                                    animate={{ opacity: 1 }}
                                                    className="text-danger small mt-1 d-flex align-items-center"
                                                >
                                                    <FaExclamationTriangle
                                                        className="me-1"
                                                        size={10}
                                                    />
                                                    {formik.errors.newPassword}
                                                </motion.div>
                                            )}
                                    </motion.div>
                                )}
                            </motion.div>

                            {/* Confirm Password */}
                            <motion.div variants={itemVariants} className="mb-3">
                                <label
                                    htmlFor="confirmPassword"
                                    className="form-label fw-semibold small"
                                    style={{ color: isDarkMode ? '#e5e7eb' : '#111827' }}
                                >
                                    Confirm New Password
                                </label>
                                <div className="input-group input-group-sm">
                                    <input
                                        id="confirmPassword"
                                        name="confirmPassword"
                                        type={showPasswords.confirm ? 'text' : 'password'}
                                        value={formik.values.confirmPassword}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        className={`form-control form-control-sm ${formik.touched.confirmPassword &&
                                            formik.errors.confirmPassword
                                            ? 'is-invalid'
                                            : ''
                                            } ${formik.touched.confirmPassword &&
                                                !formik.errors.confirmPassword
                                                ? 'is-valid'
                                                : ''
                                            }`}
                                        placeholder="Confirm new password"
                                        style={{
                                            fontSize: '0.875rem',
                                            background: isDarkMode ? '#020617' : '#ffffff',
                                            color: isDarkMode ? '#e5e7eb' : '#111827',
                                            borderColor: isDarkMode ? '#334155' : undefined
                                        }}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => togglePasswordVisibility('confirm')}
                                        className="btn btn-outline-secondary border"
                                        style={{
                                            fontSize: '0.75rem',
                                            background: isDarkMode ? '#020617' : '#ffffff',
                                            color: isDarkMode ? '#e5e7eb' : '#6c757d',
                                            borderColor: isDarkMode ? '#4b5563' : undefined
                                        }}
                                    >
                                        {showPasswords.confirm ? (
                                            <FaEyeSlash size={14} />
                                        ) : (
                                            <FaEye size={14} />
                                        )}
                                    </button>
                                </div>
                                {formik.touched.confirmPassword &&
                                    formik.errors.confirmPassword && (
                                        <motion.div
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            className="text-danger small mt-1 d-flex align-items-center"
                                        >
                                            <FaExclamationTriangle className="me-1" size={10} />
                                            {formik.errors.confirmPassword}
                                        </motion.div>
                                    )}
                                {formik.values.confirmPassword &&
                                    !formik.errors.confirmPassword && (
                                        <motion.div
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            className="text-success small mt-1 d-flex align-items-center"
                                        >
                                            <FaCheck className="me-1" size={10} />
                                            Passwords match
                                        </motion.div>
                                    )}
                            </motion.div>

                            {/* Logout Checkbox */}
                            <motion.div variants={itemVariants} className="mb-4">
                                <div className="form-check">
                                    <input
                                        id="logoutFromSession"
                                        name="logoutFromSession"
                                        type="checkbox"
                                        checked={formik.values.logoutFromSession}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        className={`form-check-input ${formik.touched.logoutFromSession &&
                                            formik.errors.logoutFromSession
                                            ? 'is-invalid'
                                            : ''
                                            }`}
                                    />
                                    <label
                                        htmlFor="logoutFromSession"
                                        className="form-check-label small"
                                        style={{ color: isDarkMode ? '#e5e7eb' : '#111827' }}
                                    >
                                        <strong>
                                            Log out from this session after password change
                                        </strong>
                                        <div
                                            style={{
                                                fontSize: '0.75rem',
                                                color: isDarkMode ? '#9ca3af' : '#6c757d'
                                            }}
                                        >
                                            For security reasons, you will need to log in again
                                            with your new password
                                        </div>
                                    </label>
                                </div>
                                {formik.touched.logoutFromSession &&
                                    formik.errors.logoutFromSession && (
                                        <motion.div
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            className="text-danger small mt-1 d-flex align-items-center"
                                        >
                                            <FaExclamationTriangle className="me-1" size={10} />
                                            {formik.errors.logoutFromSession}
                                        </motion.div>
                                    )}
                            </motion.div>

                            {/* Submit Button */}
                            <motion.div variants={itemVariants}>
                                <motion.button
                                    type="submit"
                                    disabled={!isFormValid() || isSubmitting}
                                    whileHover={{ scale: isFormValid() ? 1.02 : 1 }}
                                    whileTap={{ scale: isFormValid() ? 0.98 : 1 }}
                                    className="btn w-100 fw-semibold py-2"
                                    style={{
                                        ...styles.submitBtn,
                                        fontSize: '0.9rem',
                                        opacity: isFormValid() ? 1 : 0.6
                                    }}
                                >
                                    {isSubmitting ? (
                                        <>
                                            <span
                                                className="spinner-border spinner-border-sm me-2"
                                                role="status"
                                            />
                                            Updating...
                                        </>
                                    ) : (
                                        'Update Password'
                                    )}
                                </motion.button>
                            </motion.div>
                        </motion.form>
                    </motion.div>

                    <motion.div variants={MOTION.item} className="p-3" style={styles.card}>
                            <div className="d-flex align-items-center mb-2">
                                <FaShieldAlt className="text-primary me-2" size={14} />
                                <h6
                                    className="mb-0 fw-semibold small"
                                    style={{ color: isDarkMode ? '#e5e7eb' : '#111827' }}
                                >
                                    Password Requirements:
                                </h6>
                            </div>
                            <ul
                                className="list-unstyled mb-0 small"
                                style={{
                                    fontSize: '0.75rem',
                                    color: isDarkMode ? '#9ca3af' : '#6c757d'
                                }}
                            >
                                <li className="mb-1 d-flex align-items-center">
                                    <FaCheck className="text-success me-2" size={10} />
                                    Minimum 8 characters long
                                </li>
                                <li className="mb-1 d-flex align-items-center">
                                    <FaCheck className="text-success me-2" size={10} />
                                    Uppercase & lowercase letters
                                </li>
                                <li className="mb-1 d-flex align-items-center">
                                    <FaCheck className="text-success me-2" size={10} />
                                    At least one number
                                </li>
                                <li className="mb-1 d-flex align-items-center">
                                    <FaCheck className="text-success me-2" size={10} />
                                    One special character (!@#$%^&*)
                                </li>
                                <li className="mb-0 d-flex align-items-center">
                                    <FaCheck className="text-success me-2" size={10} />
                                    Different from current password
                                </li>
                            </ul>
                    </motion.div>
                </motion.div>
            </div>
        </motion.div>
    );
};

export default ChangePassword;
